/**
 * ZenMarkup Privacy Analytics — Overview Widgets module.
 *
 * Peak Hours bar chart and Devices card for the Overview tab.
 */

import { setWidgetContent, emptyState } from './table-builder.js';
import { formatNumber } from './charts.js';
import { render as renderDevices } from './overview-devices.js';

let hasAnimatedBars = false;
const BAR_STAGGER = 35;

/** Hours that get a visible x-axis label. */
const LABELED_HOURS = { 0: true, 6: true, 12: true, 18: true };

/**
 * Format hour number as HH:00 string.
 *
 * @param {number} h  Hour (0–23).
 * @return {string}
 */
function formatHour(h) {
	return (h < 10 ? '0' : '') + h + ':00';
}

/**
 * Render Peak Hours vertical bar chart for Overview tab.
 *
 * @param {Object} data  Full stats response data.
 */
function renderPeakHours(data) {
	const ph = data.peak_hours;

	if (!ph || !ph.hours || !ph.hours.length || !ph.peak_visitors) {
		setWidgetContent('peak-hours', emptyState('No hourly data yet.'));
		return;
	}

	const hours        = ph.hours;
	const peakVisitors = ph.peak_visitors;
	const maxVal       = peakVisitors || 1;

	const wrapper = document.createElement('div');
	wrapper.className = 'zmpa-peak-hours';

	const chart = document.createElement('div');
	chart.className = 'zmpa-ph-chart';

	const tooltip = document.createElement('div');
	tooltip.className = 'zmpa-ph-tooltip';
	tooltip.style.display = 'none';
	chart.appendChild(tooltip);

	const animate = !hasAnimatedBars;
	const bars = [];

	hours.forEach(function (item, idx) {
		const col = document.createElement('div');
		col.className = 'zmpa-ph-col';

		const bar = document.createElement('div');
		bar.className = 'zmpa-ph-bar';
		const pct = maxVal > 0 ? (item.visitors / maxVal) * 100 : 0;
		const targetHeight = pct > 0 ? Math.max(pct, 3) + '%' : '2px';

		if (animate) {
			bar.style.height = '0%';
			bars.push({ el: bar, target: targetHeight, delay: idx * BAR_STAGGER });
		} else {
			bar.style.height = targetHeight;
		}

		if (item.visitors > 0 && maxVal > 0) {
			var ratio = item.visitors / maxVal;
			var level = ratio >= 0.85 ? 4 : ratio >= 0.6 ? 3 : ratio >= 0.4 ? 2 : ratio >= 0.2 ? 1 : 0;
			bar.setAttribute('data-intensity', level);
		}

		col.addEventListener('mouseenter', function () {
			tooltip.textContent = formatHour(item.hour) + ' \u00b7 ' + formatNumber(item.visitors) + ' visitors';
			tooltip.style.display = 'block';

			var chartRect = chart.getBoundingClientRect();
			var colRect = col.getBoundingClientRect();
			var tipWidth = tooltip.offsetWidth;
			var left = colRect.left - chartRect.left + (colRect.width / 2) - (tipWidth / 2);

			if (left < 0) { left = 0; }
			if (left + tipWidth > chartRect.width) { left = chartRect.width - tipWidth; }

			tooltip.style.left = left + 'px';
		});

		col.addEventListener('mouseleave', function () {
			tooltip.style.display = 'none';
		});

		col.appendChild(bar);
		chart.appendChild(col);
	});

	wrapper.appendChild(chart);

	const xAxis = document.createElement('div');
	xAxis.className = 'zmpa-ph-xaxis';

	for (var h = 0; h < 24; h++) {
		const lbl = document.createElement('span');
		lbl.className = 'zmpa-ph-xlabel';
		lbl.textContent = LABELED_HOURS[h] ? h : '';
		xAxis.appendChild(lbl);
	}

	wrapper.appendChild(xAxis);

	setWidgetContent('peak-hours', wrapper);

	if (animate && bars.length) {
		hasAnimatedBars = true;
		requestAnimationFrame(function () {
			bars.forEach(function (b) {
				setTimeout(function () { b.el.style.height = b.target; }, b.delay);
			});
		});
	}
}

/**
 * Render all Overview widgets.
 *
 * @param {Object} data  Response from API fetchStats().
 */
export function render(data) {
	renderPeakHours(data);
	renderDevices(data);
}
